import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import InteractiveForm from './components/InteractiveForm';
import './App.css';

const { FiArrowDown, FiCheck, FiStar, FiLayout, FiSearch, FiSmartphone, FiFileText, FiZap, FiShield, FiArrowUpRight, FiPlus, FiMinus, FiFacebook, FiInstagram, FiLinkedin, FiMail, FiPhone, FiLock, FiAward, FiTrendingUp, FiMessageCircle, FiGlobe } = FiIcons;

function App() {
  const [showForm, setShowForm] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [displayedText, setDisplayedText] = useState('');
  const [openFAQ, setOpenFAQ] = useState(null);
  const fullText = 'עמוד נחיתה לעסק שלכם';

  useEffect(() => {
    // Simulate loading
    setTimeout(() => setIsLoading(false), 1500);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      let index = 0;
      const typingInterval = setInterval(() => {
        if (index <= fullText.length) {
          setDisplayedText(fullText.slice(0, index));
          index++;
        } else {
          clearInterval(typingInterval);
        }
      }, 100);
      return () => clearInterval(typingInterval);
    }
  }, [isLoading]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const features = [
    {
      title: "עיצוב מותאם למותג",
      description: "עיצוב ייחודי שמשקף את הזהות העסקית שלכם",
      icon: FiLayout,
      color: "from-sitechs-blue to-accent-cyan"
    },
    {
      title: "אופטימיזציה למנועי חיפוש",
      description: "נוודא שהלקוחות ימצאו אתכם בגוגל",
      icon: FiSearch,
      color: "from-accent-purple to-accent-pink"
    },
    {
      title: "מותאם 100% למובייל",
      description: "חוויה מושלמת בכל מכשיר ומסך",
      icon: FiSmartphone,
      color: "from-accent-pink to-accent-yellow"
    },
    {
      title: "טופס לידים חכם",
      description: "טופס אינטראקטיבי שמגדיל המרות",
      icon: FiFileText,
      color: "from-sitechs-darkblue to-sitechs-blue"
    },
    {
      title: "טעינה מהירה",
      description: "ביצועים מעולים לחוויית גלישה מושלמת",
      icon: FiZap,
      color: "from-accent-yellow to-sitechs-blue"
    },
    {
      title: "אבטחה מתקדמת",
      description: "הגנה מלאה על המידע העסקי שלכם",
      icon: FiShield,
      color: "from-sitechs-gray to-sitechs-darkgray"
    }
  ];

  const testimonials = [
    {
      name: "רונן כהן",
      business: "מאמן כושר אישי",
      text: "תוך שבוע הכפלתי את כמות הלידים! העמוד פשוט עובד מצוין",
      rating: 5,
      image: "/images/testimonials/ronen_cohen.jpg"
    },
    {
      name: "מיכל לוי",
      business: "קוסמטיקאית",
      text: "ההשקעה הכי טובה שעשיתי לעסק. התוצאות מדברות בעד עצמן",
      rating: 5,
      image: "/images/testimonials/michal_levy.jpg"
    },
    {
      name: "דני שמש",
      business: "בעל מסעדה",
      text: "העמוד הביא לי 40% יותר הזמנות בחודש הראשון. מדהים!",
      rating: 5,
      image: "/images/testimonials/dani_shemesh.jpg"
    },
    {
      name: "שרה אברהם",
      business: "מעצבת פנים",
      text: "העיצוב המקצועי והנקי גרם ללקוחות לסמוך עליי מהרגע הראשון",
      rating: 5,
      image: "/images/testimonials/sara_avraham.jpg"
    },
    {
      name: "יוסי דוד",
      business: "יועץ עסקי",
      text: "קיבלתי בדיוק את מה שרציתי ואפילו יותר. שירות מעולה!",
      rating: 5,
      image: "/images/testimonials/yossi_david.png"
    },
    {
      name: "נעמה גולן",
      business: "מורה ליוגה",
      text: "העמוד עזר לי למלא את הכיתות שלי תוך זמן קצר. ממליצה בחום!",
      rating: 5,
      image: "/images/testimonials/naama_golan.jpg"
    }
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => 
        (prev + 2) >= testimonials.length ? 0 : prev + 2
      );
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const faqItems = [
    {
      question: "כמה זמן לוקח לבנות עמוד נחיתה?",
      answer: "אנחנו מתחייבים לספק עמוד נחיתה מוכן תוך 5 ימי עבודה מרגע קבלת כל הפרטים הנדרשים."
    },
    {
      question: "מה כולל המחיר של 1,000 ₪?",
      answer: "הכל! עיצוב מותאם אישית, כתיבת תוכן שיווקי, אופטימיזציה למנועי חיפוש, טופס לידים אינטראקטיבי, ואירוח מהיר לשנה שלמה - חינם (שווי 360 ₪)."
    },
    {
      question: "מה קורה אחרי השנה הראשונה?",
      answer: "אירוח ותחזוקה ב-360 ₪ לשנה. דומיין (אם נדרש) - 80 ₪ לשנה. ללא התחייבות - תוכלו להעביר את העמוד לכל מקום."
    },
    {
      question: "כמה זמן המבצע בתוקף?",
      answer: "המבצע מוגבל לחודש הקרוב. המחיר הרגיל הוא 2,500 ₪, כרגע רק 1,000 ₪ - חיסכון של 60%!"
    },
    {
      question: "האם אצטרך לספק תוכן ותמונות?",
      answer: "נכתוב את כל התוכן השיווקי המקצועי. תמונות - או שלכם או מבנק תמונות מקצועי שלנו."
    },
    {
      question: "מה אם ארצה שינויים?",
      answer: "כלולים 3 סבבי תיקונים במחיר. אחרי ההשקה - תמיכה מלאה למשך חודש ללא עלות נוספת."
    }
  ];

  return (
    <>
      {/* Absolute logo at top right */}
      <img
        src="/images/sitechs_logo_mini-light.png"
        aria-label="סייטקס פיתוח" 
        style={{
          width: '140px',
          maxWidth: '30vw',
          height: 'auto',
          zIndex: 1000
        }}
        className="
          absolute
          top-4 left-2
          md:top-4 md:left-auto
          md:right-4
          select-none pointer-events-none
        "
      />
      {/* Loading Screen */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-sitechs-darkgray z-[100] flex items-center justify-center"
          >
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 180, 360],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="relative"
            >
              <div className="w-24 h-24 border-4 border-sitechs-blue border-t-transparent rounded-full animate-spin"></div>
              <div className="absolute inset-0 w-24 h-24 border-4 border-accent-pink border-b-transparent rounded-full animate-spin-slow"></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="min-h-screen bg-sitechs-darkgray overflow-x-hidden" dir="rtl">
      {/* Animated Background Particles */}
      <div className="fixed inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 15}s`,
              animationDuration: `${15 + Math.random() * 10}s`
            }}
          />
        ))}
      </div>

      {/* Mouse Follow Gradient */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-30"
        style={{
          background: `radial-gradient(600px at ${mousePosition.x}px ${mousePosition.y}px, rgba(46, 184, 230, 0.15), transparent 40%)`
        }}
      />


      {/* Hero Section */}
      <section className="relative pb-8 md:py-32 pt-24 overflow-hidden">
        <div className="absolute inset-0 cyber-grid opacity-20"></div>
        
        {/* Animated Shapes */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-sitechs-blue/20 rounded-full morph-animation blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent-purple/20 rounded-full morph-animation blur-3xl" style={{ animationDelay: '4s' }}></div>
        
        {/* Floating Elements */}
        <motion.div
          className="absolute top-40 left-20"
          animate={{ 
            y: [0, -30, 0],
            rotate: [0, 360]
          }}
          transition={{ 
            duration: 10,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          <div className="w-20 h-20 bg-gradient-to-br from-sitechs-blue to-accent-cyan rounded-lg opacity-20 blur-sm"></div>
        </motion.div>
        
        <motion.div
          className="absolute bottom-40 right-32"
          animate={{ 
            y: [0, 30, 0],
            rotate: [360, 0]
          }}
          transition={{ 
            duration: 12,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          <div className="w-16 h-16 bg-gradient-to-br from-accent-pink to-accent-purple rounded-full opacity-20 blur-sm"></div>
        </motion.div>
        
        {/* Animated Lines */}
        <div className="absolute top-0 left-0 w-full h-1 glow-line"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 glow-line" style={{ animationDelay: '1.5s' }}></div>
        
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center relative"
          >
            {/* Floating price tag - positioned above the title */}
            {/* <motion.div
              className="inline-block mb-8 bg-accent-pink text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg"
              animate={{ 
                y: [0, -5, 0],
                rotate: [-5, 5, -5]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              חסכון 80%! 🔥
            </motion.div> */}


            {/* Price Badge - מבצע מיוחד */}
            <motion.div
              className="inline-block mb-8 bg-gradient-to-r from-accent-pink to-accent-purple text-white px-8 py-4 rounded-full text-lg font-bold shadow-2xl"
              animate={{ 
                y: [0, -5, 0],
                scale: [1, 1.05, 1]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              💎 מחיר מבצע לזמן מוגבל!
            </motion.div>
            

            <motion.div
              animate={{ 
                scale: [1, 1.02, 1],
                rotate: [0, 0.5, -0.5, 0]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              whileHover={{
                scale: 1.05,
                transition: { duration: 0.2 }
              }}
            >
              <h1 className="text-6xl md:text-8xl font-bold mb-6 cursor-default">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-sitechs-blue via-accent-cyan to-sitechs-blue animate-gradient">
                  {displayedText}
                  <motion.span
                    animate={{ opacity: [1, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                    className="inline-block w-1 h-16 bg-sitechs-blue ml-2"
                  />
                </span>
                <br />
                <motion.span 
                  className="text-transparent bg-clip-text bg-gradient-to-r from-accent-pink via-accent-purple to-accent-yellow animate-gradient relative glitch-text"
                  data-text="רק ב-1000 ₪"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ 
                    opacity: 1,
                    scale: 1,
                    textShadow: [
                      "0 0 20px rgba(255, 59, 92, 0.5)",
                      "0 0 40px rgba(147, 51, 234, 0.5)",
                      "0 0 20px rgba(255, 217, 61, 0.5)",
                      "0 0 20px rgba(255, 59, 92, 0.5)"
                    ]
                  }}
                  transition={{ 
                    opacity: { delay: 1.5, duration: 0.5 },
                    scale: { delay: 1.5, duration: 0.5, type: "spring", stiffness: 200 },
                    textShadow: { duration: 3, repeat: Infinity }
                  }}
                >
                  רק ב-1000 ₪
                </motion.span>
              </h1>
            </motion.div>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-xl py-2 md:py-4 text-gray-300 mb-4 max-w-3xl mx-auto"
            >
              פיתוח עמודי נחיתה ממוקדי המרות לעסקים קטנים וגדולים
              <br />
              <span className="font-semibold text-sitechs-blue neon-glow">5 ימי עבודה • תוצאה מקצועית</span>
            </motion.p>

            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative group bg-gradient-to-r from-sitechs-blue to-sitechs-darkblue text-white px-12 py-6 rounded-full text-lg font-bold overflow-hidden glow-button"
              onClick={() => {
                document.getElementById('form').scrollIntoView({ behavior: 'smooth' });
                setShowForm(true);
              }}
            >
              <span className="relative z-10 flex items-center gap-2">
                צור עמוד נחיתה עכשיו
                <motion.span
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  ←
                </motion.span>
              </span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-accent-pink via-accent-purple to-accent-cyan opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                animate={{
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                }}
                transition={{ duration: 3, repeat: Infinity }}
                style={{ backgroundSize: "200% 200%" }}
              />
              <div className="absolute inset-0 neon-box-glow opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </motion.button>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2 }}
              className="mt-6 text-gray-400"
            >
              ייעוץ ראשוני ללא עלות • בואו נבנה ביחד את ההצלחה שלכם
            </motion.p>

            {/* Scroll Indicator */}
            <motion.div
              className="mt-16 flex justify-center"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <SafeIcon icon={FiArrowDown} className="text-3xl text-sitechs-blue opacity-50" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-16 bg-gradient-to-r from-sitechs-darkgray via-sitechs-gray/30 to-sitechs-darkgray">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="flex flex-wrap justify-center gap-8 md:gap-16 items-center"
          >
            {[
              { icon: FiLock, text: "תשלום מאובטח", color: "from-green-500 to-emerald-600" },
              { icon: FiAward, text: "17 שנות ניסיון", color: "from-yellow-500 to-orange-600" },
              { icon: FiTrendingUp, text: "347 פרויקטים", color: "from-sitechs-blue to-sitechs-darkblue" },
              { icon: FiShield, text: "אירוח שנה חינם", color: "from-purple-500 to-pink-600" }
            ].map((badge, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ delay: index * 0.1, type: "spring", stiffness: 200 }}
                whileHover={{ scale: 1.1, y: -5 }}
                className="flex items-center gap-3 group"
              >
                <div className={`p-3 bg-gradient-to-br ${badge.color} rounded-xl shadow-lg group-hover:shadow-2xl transition-all`}>
                  <SafeIcon icon={badge.icon} className="text-2xl text-white" />
                </div>
                <span className="text-gray-300 font-medium group-hover:text-white transition-colors">
                  {badge.text}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 relative overflow-hidden bg-gradient-to-b from-sitechs-darkgray to-sitechs-gray/20">
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sitechs-blue to-accent-cyan mb-4">
              למה לבחור בנו?
            </h2>
            <p className="text-xl text-gray-400">
              מקבלים הרבה יותר מסתם עמוד נחיתה
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, rotateY: -30 }}
                whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10, rotateY: 5 }}
                className="group relative"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-20 group-hover:opacity-30 rounded-2xl blur-xl transition-opacity`}></div>
                <div className="relative bg-sitechs-gray/50 backdrop-blur-md p-8 rounded-2xl border border-gray-700 hover:border-sitechs-blue/50 transition-all duration-300 card-3d">
                  <motion.div 
                    className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${feature.color} mb-4 relative`}
                    whileHover={{ rotate: 360, scale: 1.2 }}
                    transition={{ duration: 0.6 }}
                  >
                    <SafeIcon icon={feature.icon} className="text-3xl text-white" />
                    <motion.div
                      className="absolute inset-0 rounded-xl"
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  </motion.div>
                  <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-sitechs-blue group-hover:to-accent-cyan transition-all">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                    {feature.description}
                  </p>
                  <motion.div
                    className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity"
                    initial={{ scale: 0, rotate: -180 }}
                    whileHover={{ scale: 1.2, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 200 }}
                  >
                    <SafeIcon icon={FiArrowUpRight} className="text-sitechs-blue" />
                  </motion.div>
                  
                  {/* Hover glow effect */}
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-transparent via-sitechs-blue/10 to-transparent"></div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Process Section */}
      <section id="process" className="py-32 relative overflow-hidden bg-gradient-to-b from-sitechs-gray/20 to-sitechs-darkgray" style={{ textAlign: 'center' }} >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-accent-pink to-accent-purple mb-4">
              איך זה עובד?
            </h2>
            <p className="text-xl text-gray-400">תהליך פשוט ומהיר</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { number: "01", title: "מלאו את הטופס", desc: "ספרו לנו על העסק שלכם" },
              { number: "02", title: "אנחנו בונים", desc: "תוך 5 ימי עבודה" },
              { number: "03", title: "השקה והצלחה", desc: "העמוד שלכם באוויר!" }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="relative group"
              >
                <motion.div
                  className="text-8xl font-bold text-sitechs-blue/20 mb-4 relative"
                  whileHover={{ scale: 1.2, rotate: 5 }}
                >
                  {step.number}
                  <motion.div
                    className="absolute inset-0 text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sitechs-blue to-accent-cyan opacity-0 group-hover:opacity-100 transition-opacity"
                    animate={{
                      backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                    style={{ backgroundSize: "200% 200%" }}
                  >
                    {step.number}
                  </motion.div>
                </motion.div>
                <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-sitechs-blue transition-colors">
                  {step.title}
                </h3>
                <p className="text-gray-400">{step.desc}</p>
                {index < 2 && (
                  <motion.div
                    className="hidden md:block absolute top-20 right-full w-20 text-sitechs-blue opacity-50"
                    animate={{ 
                      x: [-5, 5, -5],
                      opacity: [0.5, 1, 0.5]
                    }}
                    style={{ right: '90%' }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <svg className="w-full" viewBox="0 0 100 20" transform="scale(-1, 1)">
                      <motion.path
                        d="M 0 10 Q 50 0 100 10"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        strokeDasharray="5 5"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                    </svg>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="form" className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sitechs-darkgray via-sitechs-gray to-sitechs-darkgray"></div>
        <div className="absolute inset-0 cyber-grid opacity-10"></div>
        
        <div className="container mx-auto px-4 relative">
          {showForm ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <InteractiveForm />
            </motion.div>
          ) : (
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sitechs-blue to-accent-cyan mb-8">
                הצעד הראשון להגדלת המכירות
              </h2>
              <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
                רוצים לדעת איך עמוד נחיתה יכול לשנות את העסק שלכם?
                השאירו פרטים מדוייקים ככל הניתן עבור העסק שלכם ונבנה יחד אסטרטגיה מנצחת.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowForm(true)}
                className="relative group bg-white text-sitechs-darkgray px-12 py-6 rounded-full text-lg font-bold overflow-hidden hover:text-white transition-colors"
              >
                <span className="relative z-10">בואו נתחיל - מלאו את טופס המידע אודות העסק שלכם</span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-sitechs-blue to-accent-purple scale-x-0 group-hover:scale-x-100 transition-transform origin-left"
                />
              </motion.button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-32 relative overflow-hidden bg-gradient-to-b from-sitechs-darkgray to-sitechs-gray">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.h2 
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-accent-yellow to-sitechs-blue mb-12"
            >
              מה אומרים עלינו
            </motion.h2>
            
            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonialIndex} 
                  initial={{ opacity: 1, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5 }}
                  className="grid md:grid-cols-2 gap-8"
                >
                  {testimonials.slice(currentTestimonialIndex, currentTestimonialIndex + 2).map((testimonial, localIndex) => {
                    const globalIndex = currentTestimonialIndex + localIndex;
                    return (
                      <motion.div
                        key={globalIndex}
                        initial={{ opacity: 1, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: localIndex * 0.1 }}
                        whileHover={{ scale: 1.05, rotate: localIndex % 2 === 0 ? 2 : -2 }}
                        className="relative group"
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-sitechs-blue/20 to-accent-purple/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="relative bg-sitechs-gray/30 backdrop-blur-md p-6 lg:p-8 rounded-2xl border border-gray-700 hover:border-sitechs-blue/50 transition-all">
                          <div className="flex flex-col lg:flex-row items-center lg:items-start mb-4 text-center lg:text-right">
                            
                            {/* איזור תמונה ושם */}
                            <div className="flex items-center justify-center lg:justify-start mb-2 lg:mb-0">
                              <motion.div
                                initial={{ scale: 0.7 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                                className="relative"
                              >
                                <div className="absolute inset-0 bg-gradient-to-br from-sitechs-blue to-accent-purple rounded-full opacity-50 blur-md scale-110"></div>
                                <img 
                                  src={testimonial.image}
                                  alt={testimonial.name}
                                  className="relative w-16 h-16 rounded-full object-cover border-2 border-white/20 shadow-lg"
                                  onError={(e) => {
                                    e.target.src = '/images/testimonials/default-avatar.png';
                                  }}
                                />
                              </motion.div>

                              <div className="flex-1 mr-3">
                                <p className="font-semibold text-white text-lg">{testimonial.name}</p>
                                <p className="text-sitechs-blue">{testimonial.business}</p>
                              </div>
                            </div>

                            {/* איזור הכוכבים */}
                            <div className="flex gap-0.5 justify-center lg:mr-auto lg:mr-auto mt-3 lg:mt-0">
                              {[...Array(testimonial.rating)].map((_, i) => (
                                <motion.div
                                  key={i}
                                  initial={{ opacity: 0, scale: 0 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: 0.3 + i * 0.05 }}
                                >
                                  <SafeIcon icon={FiStar} className="text-accent-yellow text-lg fill-current" />
                                </motion.div>
                              ))}
                            </div>
                          </div>

                          <p className="text-gray-300 mb-4 text-lg italic text-center">"{testimonial.text}"</p>
                        </div>

                      </motion.div>
                    );
                  })}
                </motion.div>
              </AnimatePresence>
              
              {/* Carousel indicators */}
              <div className="flex justify-center gap-2 mt-8">
                {Array.from({ length: Math.ceil(testimonials.length / 2) }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonialIndex(index * 2)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      currentTestimonialIndex === index * 2 
                        ? 'w-8 bg-sitechs-blue' 
                        : 'bg-gray-600 hover:bg-gray-500'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-32 relative overflow-hidden bg-gradient-to-b from-sitechs-gray to-sitechs-darkgray">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sitechs-blue to-accent-cyan mb-4">
              שאלות נפוצות
            </h2>
            <p className="text-xl text-gray-400">כל מה שרצית לדעת</p>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <motion.div
                  className="bg-sitechs-gray/30 backdrop-blur-md rounded-2xl border border-gray-700 overflow-hidden hover:border-sitechs-blue/50 transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                >
                  <button
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    className="w-full px-8 py-6 flex items-center justify-between text-left"
                  >
                    <h3 className="text-lg font-semibold text-white group-hover:text-sitechs-blue transition-colors">
                      {item.question}
                    </h3>
                    <motion.div
                      animate={{ rotate: openFAQ === index ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <SafeIcon 
                        icon={openFAQ === index ? FiMinus : FiPlus} 
                        className="text-2xl text-sitechs-blue"
                      />
                    </motion.div>
                  </button>
                  <AnimatePresence>
                    {openFAQ === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-8 pb-6"
                      >
                        <p className="text-gray-400 leading-relaxed">
                          {item.answer}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-r from-sitechs-blue via-sitechs-darkblue to-sitechs-blue">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjZmZmZmZmMjAiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              מוכנים להצליח באינטרנט?
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              הצטרפו למאות עסקים שכבר משיגים תוצאות מדהימות עם עמודי הנחיתה שלנו
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-sitechs-darkblue px-12 py-6 rounded-full text-lg font-bold hover:bg-gray-100 transition-all shadow-2xl"
              onClick={() => {
                document.getElementById('form').scrollIntoView({ behavior: 'smooth' });
                setShowForm(true);
              }}
            >
              התחילו עכשיו - רק 1000 ₪
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-sitechs-darkgray border-t border-gray-800 py-16 relative overflow-hidden">
        <div className="absolute inset-0 cyber-grid opacity-5"></div>
        <div className="container mx-auto px-4 relative">
          <div className="grid md:grid-cols-3 gap-8 mb-12 text-center md:text-right">
            {/* Logo and Description */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col items-center md:items-start"
            >
              <a href="https://sitechs.co.il" className="mb-4" alt="סייטקס פיתוח">  
                <img 
                  src="/images/sitechs_logo_mini-light.png"
                  aria-label="סייטקס פיתוח"
                  className="h-16 mb-4 float-animation"
                  style={{ maxWidth: '100px', height: 'auto' }}
                  onError={(e) => {
                    console.error('Footer logo failed to load');
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div class="h-16 mb-4 text-3xl font-bold text-sitechs-blue">S</div>' + e.target.parentElement.innerHTML.substring(e.target.outerHTML.length);
                  }}
                />
              </a>
              <p className="text-gray-400 mb-4">
                צוות של מפתחים ומעצבים עם ניסיון של 17 שנה בפיתוח דיגיטלי. אנחנו מאמינים בפתרונות פשוטים, יעילים ובמחיר הוגן.
              </p>
              <div className="flex gap-4">
                {[
                  {
                    icon: FiFacebook,
                    link: "https://www.facebook.com/Sitechs",
                    label: "Facebook",
                    newTab: true,
                  },
                  {
                    // Feather לא כולל לוגו WhatsApp, נשתמש בבועת צ'אט
                    icon: FiMessageCircle,
                    // מספר בינלאומי ללא 0 מוביל: 972584567877
                    // מקודדים את הטקסט כדי שלא יהיו תווים שבורים ב-URL
                    link: `https://wa.me/972584567877?text=${encodeURIComponent("מתעניין בעמוד נחיתה למוצר שלי")}`,
                    label: "WhatsApp",
                    newTab: true,
                  },
                  {
                    // אייקון לפתיחת אתר
                    icon: FiGlobe,
                    link: "https://sitechs.co.il",
                    label: "Website",
                    newTab: true,
                  },
                ].map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.link}
                    target={social.newTab ? "_blank" : undefined}
                    rel={social.newTab ? "noopener noreferrer" : undefined}
                    aria-label={social.label}
                    whileHover={{ scale: 1.2, rotate: 5 }}
                    className="w-10 h-10 bg-sitechs-gray/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-sitechs-blue hover:bg-sitechs-blue/20 transition-all"
                  >
                    <SafeIcon icon={social.icon} className="text-lg" />
                  </motion.a>
                ))}
              </div>
            </motion.div>
            
            {/* Services */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex flex-col items-center md:items-start"
            >
              <h3 className="text-xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-sitechs-blue to-accent-cyan">
                השירותים שלנו
              </h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-sitechs-blue transition-colors">פיתוח עמודי נחיתה</a></li>
                <li><a href="#" className="hover:text-sitechs-blue transition-colors">אתרי תדמית</a></li>
                <li><a href="#" className="hover:text-sitechs-blue transition-colors">מערכות ניהול תוכן</a></li>
                <li><a href="#" className="hover:text-sitechs-blue transition-colors">פיתוח תוכנות מותאמות לעסקים</a></li>
                <li><a href="#" className="hover:text-sitechs-blue transition-colors">פיתוח אפליקציות</a></li>
                <li><a href="#" className="hover:text-sitechs-blue transition-colors">פתרונות e-commerce</a></li>
                <li><a href="#" className="hover:text-sitechs-blue transition-colors">שיווק דיגיטלי</a></li>
              </ul>
            </motion.div>
            
            {/* Contact */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-col items-center md:items-start"
            >
              <h3 className="text-xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-sitechs-blue to-accent-cyan">
                צור קשר
              </h3>
              <div className="space-y-3">
                <a href="tel:058-4567877" className="flex items-center gap-3 text-gray-400 hover:text-sitechs-blue transition-colors group">
                  <motion.div
                    whileHover={{ rotate: 15 }}
                    className="w-10 h-10 bg-sitechs-gray/50 rounded-lg flex items-center justify-center group-hover:bg-sitechs-blue/20 transition-all"
                  >
                    <SafeIcon icon={FiPhone} className="text-lg" />
                  </motion.div>
                  <span>058-4567877</span>
                </a>
                <a href="mailto:service@sitechs.co.il" className="flex items-center gap-3 text-gray-400 hover:text-sitechs-blue transition-colors group">
                  <motion.div
                    whileHover={{ rotate: 15 }}
                    className="w-10 h-10 bg-sitechs-gray/50 rounded-lg flex items-center justify-center group-hover:bg-sitechs-blue/20 transition-all"
                  >
                    <SafeIcon icon={FiMail} className="text-lg" />
                  </motion.div>
                  <span>service@sitechs.co.il</span>
                </a>
              </div>
              
              {/* Additional company info */}
              <div className="mt-6 text-gray-400 text-sm">
                <p>פתרונות דיגיטליים מתקדמים לעסקים</p>
                <p>© {new Date().getFullYear()} Sitechs. כל הזכויות שמורות.</p>
              </div>
            </motion.div>
          </div>
          
          {/* Bottom bar */}
          <div className="pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex gap-6 text-sm">
                <a href="/terms"   className="text-gray-500 hover:text-sitechs-blue transition-colors">תנאי שימוש</a>
                <a href="/privacy" className="text-gray-500 hover:text-sitechs-blue transition-colors">מדיניות פרטיות</a>
              </div>
              <p className="text-gray-500 text-sm">
                Sitechs {new Date().getFullYear()} - כל הזכויות שמורות
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}

export default App;